import dicomParser from "dicom-parser";
import axiosInterceptor from "../../../helpers/Interceptor";
import { APIs } from "../../../constants/Constants";

/**
 * DICOM Encapsulated PDF support.
 *
 * An Encapsulated PDF instance is an ordinary PDF wrapped in a DICOM object so a
 * PACS can archive a report next to the scans it belongs to. It carries no pixel
 * data, so cornerstone can neither load nor render it — it is handled the same way
 * SR is (see SRModalityService): fetch the raw DICOM, parse it here, and hand the
 * viewport something it can display directly.
 *
 * The payload lives in Encapsulated Document (0042,0011) as a single OB blob; we
 * lift those bytes into a Blob. The viewport rasterises it with pdf.js (see
 * EncapsulatedPdfViewer) and the object URL backs the Open and Download actions.
 *
 * The standard patient/study tags wrapped around the payload are parsed out
 * alongside it so the viewport can show the same patient information overlay an
 * image series gets.
 */

/** SOP Class UID of the Encapsulated PDF Storage IOD. */
export const ENCAPSULATED_PDF_SOP_CLASS_UID = "1.2.840.10008.5.1.4.1.1.104.1";

/**
 * The modality the viewer routes encapsulated documents under.
 *
 * This is an internal convention, NOT a value the sender has to use. PS3.3 C.24.1
 * makes Modality (0008,0060) Type 1 for the Encapsulated Document Series Module
 * but defines it only as "the modality appropriate for the encapsulated
 * document", pointing at Defined Terms (an open list) rather than Enumerated
 * Values — its own note offers SR as appropriate for an Encapsulated CDA. So a
 * report PDF arrives labelled DOC, OT, SR or the parent study's modality, all
 * conformantly. buildSeriesStack normalises any series whose instances carry the
 * Encapsulated PDF SOP Class UID to this value, so the rest of the viewer can go
 * on matching a single modality string.
 */
export const ENCAPSULATED_DOC_MODALITY = "DOC";

/** True for a series the viewer has normalised to the document modality. */
export const isEncapsulatedDocModality = (modality) =>
  modality === ENCAPSULATED_DOC_MODALITY;

/**
 * True when a series' instances are Encapsulated PDFs, judged by SOP Class UID
 * (0008,0016) — the only reliable signal, for the reason above.
 *
 * Checks every instance rather than just the first: a series may legitimately mix
 * instances, and one encapsulated PDF is enough to mean the viewer must render it
 * as a document. Returns false when the field is absent, so a payload predicted
 * by a backend that does not yet send sopClassUID falls back to the modality.
 */
export const seriesCarriesEncapsulatedPdf = (series) =>
  Array.isArray(series?.imageList) &&
  series.imageList.some(
    (image) => image?.sopClassUID?.trim() === ENCAPSULATED_PDF_SOP_CLASS_UID
  );

const formatDate = (date) =>
  date && date.length >= 8
    ? date.slice(0, 4) + "-" + date.slice(4, 6) + "-" + date.slice(6, 8)
    : "";

const formatTime = (time) =>
  time && time.length >= 6
    ? time.slice(0, 2) + ":" + time.slice(2, 4) + ":" + time.slice(4, 6)
    : "";

/** DICOM PN is caret-delimited (FAMILY^GIVEN^MIDDLE^PREFIX^SUFFIX). */
const formatPersonName = (value) =>
  value ? value.split("^").filter(Boolean).join(" ").trim() : "";

/**
 * DICOM pads an odd-length OB value to even length with a trailing null. Some
 * encoders pad with more than one. A PDF ends at "%%EOF", so trailing nulls are
 * never part of the document — strip them rather than handing the browser's PDF
 * viewer a file with garbage after the trailer.
 */
const trimTrailingPadding = (bytes) => {
  let end = bytes.length;
  while (end > 0 && bytes[end - 1] === 0x00) end--;
  return end === bytes.length ? bytes : bytes.subarray(0, end);
};

/**
 * Copy the (0042,0011) value out of the parsed dataset.
 *
 * dicomParser hands back views onto the whole file's buffer, so this slices a
 * standalone copy — the Blob must stay valid after the parsed dataset (and the
 * multi-megabyte DICOM buffer behind it) is released.
 */
const extractDocumentBytes = (dataSet) => {
  const element = dataSet.elements.x00420011;
  if (!element || !element.length) return null;

  const view = new Uint8Array(
    dataSet.byteArray.buffer,
    dataSet.byteArray.byteOffset + element.dataOffset,
    element.length
  );
  return new Uint8Array(trimTrailingPadding(view));
};

/** A PDF always starts with the "%PDF-" signature; anything else is not one. */
const looksLikePdf = (bytes) =>
  bytes.length > 4 &&
  bytes[0] === 0x25 && // %
  bytes[1] === 0x50 && // P
  bytes[2] === 0x44 && // D
  bytes[3] === 0x46 && // F
  bytes[4] === 0x2d; //  -

const safe = (value) => (value ? value.trim() : "");

/**
 * The standard patient/study tags the Encapsulated PDF IOD wraps around the
 * payload. These feed the viewport's patient information overlay, which is the
 * only place this metadata is visible — the PDF itself is opaque to the viewer.
 */
const readDocumentMetadata = (dataSet) => {
  const contentDate = formatDate(dataSet.string("x00080023"));
  const contentTime = formatTime(dataSet.string("x00080033"));
  const studyDate = formatDate(dataSet.string("x00080020"));
  const studyTime = formatTime(dataSet.string("x00080030"));

  return {
    // Document Title (0042,0010) is the DICOM-level name for the attached file;
    // fall back to the series description when the sender leaves it empty.
    title:
      safe(dataSet.string("x00420010")) ||
      safe(dataSet.string("x0008103e")) ||
      "",
    mimeType: safe(dataSet.string("x00420012")),
    sopClassUid: safe(dataSet.string("x00080016")),

    patientName: formatPersonName(safe(dataSet.string("x00100010"))),
    patientId: safe(dataSet.string("x00100020")),
    patientBirthDate: formatDate(dataSet.string("x00100030")),
    patientSex: safe(dataSet.string("x00100040")),
    patientAge: safe(dataSet.string("x00101010")),

    studyDate: [studyDate, studyTime].filter(Boolean).join(" "),
    studyId: safe(dataSet.string("x00200010")),
    studyDescription: safe(dataSet.string("x00081030")),
    accessionNumber: safe(dataSet.string("x00080050")),
    referringPhysicianName: formatPersonName(safe(dataSet.string("x00080090"))),
    institutionName: safe(dataSet.string("x00080080")),

    modality: safe(dataSet.string("x00080060")),
    seriesNumber: safe(dataSet.string("x00200011")),
    seriesDescription: safe(dataSet.string("x0008103e")),
    instanceNumber: safe(dataSet.string("x00200013")),
    contentDateTime: [contentDate, contentTime].filter(Boolean).join(" "),
  };
};

/**
 * Fetch an Encapsulated PDF instance and turn it into something renderable.
 *
 * @param {string} path  URL of the raw DICOM object (stack.thumbnailImageIds[0],
 *                       built by buildSeriesStack against the same endpoint SR uses).
 * @returns {Promise<{objectUrl: string, blob: Blob, metadata: object}>}
 *          Resolves with an object URL the caller OWNS — it must call
 *          releaseEncapsulatedPDF() on it once the viewport stops showing the
 *          document, or the blob leaks for the lifetime of the page.
 * @throws  {Error} when the instance carries no readable PDF payload.
 */
export const loadEncapsulatedPDF = async (path) => {
  let response;
  try {
    response = await axiosInterceptor.get(path, { responseType: "blob" });
  } catch (error) {
    if (error?.response?.status === 403) {
      window.location.assign(APIs.REDIRECTLOGOUT);
      return null;
    }
    throw error;
  }

  const arrayBuffer = await response.data.arrayBuffer();
  const dataSet = dicomParser.parseDicom(new Uint8Array(arrayBuffer));

  const bytes = extractDocumentBytes(dataSet);
  if (!bytes) {
    throw new Error("Instance has no Encapsulated Document (0042,0011) value.");
  }

  const metadata = readDocumentMetadata(dataSet);

  // Trust the bytes over the declared MIME type: senders that mis-tag the MIME
  // type are common, whereas the "%PDF-" signature is definitive. Anything that
  // is genuinely not a PDF (an encapsulated CDA or STL sharing the DOC modality)
  // is rejected here rather than handed to the browser as a broken document.
  if (!looksLikePdf(bytes)) {
    const declared = metadata.mimeType || "unknown";
    throw new Error("Encapsulated document is not a PDF (mimeType=" + declared + ").");
  }

  const blob = new Blob([bytes], { type: "application/pdf" });
  return { objectUrl: URL.createObjectURL(blob), blob, metadata };
};

/** Release an object URL handed out by loadEncapsulatedPDF. Safe to call with null. */
export const releaseEncapsulatedPDF = (objectUrl) => {
  if (objectUrl) URL.revokeObjectURL(objectUrl);
};

/** Filename offered when the user downloads the document. */
export const buildPdfFileName = (metadata) => {
  const name =
    metadata?.title ||
    metadata?.seriesDescription ||
    metadata?.studyDescription ||
    "dicom-document";
  // Keep it filesystem-safe: DICOM text can legitimately contain ^ / \ and :
  return name.replace(/[^\w\-. ]+/g, "_").trim() + ".pdf";
};
